import { GlobeIcon, MailIcon, MapPinIcon, PhoneIcon, SmartphoneIcon, StarIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { contactName, type Contact, type Customer } from '@/api/customers'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PHONE_FULL_SCREEN_DIALOG } from '@/components/layout/dialogStyles'
import { FormSection } from '@/components/layout/FormSection'
import { formatAddress } from '@/components/customers/address'
import { SALUTATION_LABEL_KEYS } from '@/components/customers/labels'
import { cn } from '@/lib/utils'

/**
 * Everything on one customer, read-only. The list shows the primary contact; a customer with three
 * people at it is exactly the case where the row cannot say enough.
 *
 * A dialog, built like the lead one: same fixed header, same scrolling body, same blocks. It used
 * to slide in from the side, which made opening a row from the customer list feel like a different
 * kind of action from opening one from the lead list — two ways of doing the same thing, and the
 * eye has to learn both.
 */
export function CustomerDetailDialog({
  customer,
  onOpenChange,
}: {
  /** Null closes it. Carrying the record in the prop keeps the two from drifting apart. */
  customer: Customer | null
  onOpenChange: (open: boolean) => void
}) {
  const { t, i18n } = useTranslation()
  const language = i18n.resolvedLanguage ?? 'de'

  return (
    <Dialog open={customer !== null} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl',
          PHONE_FULL_SCREEN_DIALOG,
        )}
      >
        {customer && (
          <>
            <DialogHeader className="shrink-0 gap-1 border-b px-6 py-4 pr-12">
              <DialogTitle className="flex flex-wrap items-center gap-2">
                {customer.companyName}
                {customer.deleted && (
                  <Badge variant="outline" className="font-normal">
                    {t('customers.deleted')}
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                {customer.customerNumber
                  ? t('customers.numberIs', { number: customer.customerNumber })
                  : t('customers.noNumber')}
              </DialogDescription>
            </DialogHeader>

            <div className="grid min-h-0 flex-1 gap-8 overflow-y-auto px-6 py-5">
              <FormSection title={t('customers.detail.company')}>
                <div className="grid gap-2">
                  <Line icon={<MapPinIcon />} value={formatAddress(customer, language)} />
                  <Line icon={<PhoneIcon />} value={customer.phone} />
                  <Line icon={<MailIcon />} value={customer.email} />
                  <Line icon={<GlobeIcon />} value={customer.website} />
                  {customer.notes && (
                    <p className="text-muted-foreground mt-1 text-sm whitespace-pre-line">
                      {customer.notes}
                    </p>
                  )}
                </div>
              </FormSection>

              <FormSection
                title={t('customers.detail.contacts', { count: customer.contacts.length })}
              >
                {customer.contacts.length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t('customers.detail.noContacts')}</p>
                ) : (
                  <ul className="grid gap-3 sm:grid-cols-2">
                    {customer.contacts.map((contact) => (
                      <li key={contact.id} className="rounded-lg border px-4 py-4">
                        <ContactCard contact={contact} />
                      </li>
                    ))}
                  </ul>
                )}
              </FormSection>
            </div>

            <DialogFooter className="m-0 shrink-0 rounded-b-xl">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.close')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function ContactCard({ contact }: { contact: Contact }) {
  const { t } = useTranslation()

  return (
    <div className="grid gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{contactName(contact)}</span>
        {contact.primaryContact && (
          <Badge variant="secondary" className="gap-1 font-normal">
            <StarIcon aria-hidden />
            {t('customers.primaryContact')}
          </Badge>
        )}
      </div>
      <p className="text-muted-foreground text-xs">
        {[t(SALUTATION_LABEL_KEYS[contact.salutation]), contact.position]
          .filter(Boolean)
          .join(' · ')}
      </p>
      <Line icon={<MailIcon />} value={contact.email} />
      <Line icon={<PhoneIcon />} value={contact.phone} />
      <Line icon={<SmartphoneIcon />} value={contact.mobile} />
    </div>
  )
}

/** Renders nothing at all when there is nothing — an empty row with an icon says less than none. */
function Line({ icon, value }: { icon: ReactNode; value: string | null }) {
  if (!value) {
    return null
  }
  return (
    <p className="flex items-start gap-2 text-sm">
      <span className="text-muted-foreground mt-0.5 [&_svg]:size-3.5" aria-hidden>
        {icon}
      </span>
      <span className="break-all">{value}</span>
    </p>
  )
}
